/**
 * Ce que l'ecran montre, publie pour Lia par le MOTEUR D'ECRAN lui-meme
 * (`useScreen`) : chaque app, declaree ou codee, publie donc sa porte sans
 * rien brancher. Le shell l'ecoute pour le sous-titre du panneau et le
 * contexte envoye au serveur. Rien de ce qui voyage ici n'est une donnee de
 * fiche : un identifiant, des libelles d'interface. Le serveur relit la fiche
 * ouverte avec les droits du membre.
 */
export interface LiaScreenState {
  appId?: string;
  doorId: string;
  doorLabel: string;
  view?: string;
  search?: string;
  filters?: string[];
  archives?: boolean;
  recordId?: string;
  /** Le titre projete de la fiche ouverte : le centre de communication l'affiche (« Fiche ouverte : Ada Lovelace »). */
  recordTitle?: string;
  /** La famille du jeu de la porte (au pluriel, `jeu.family`) : l'ancre du chatter d'une fiche. */
  family?: string;
}

export const LIA_SCREEN_EVENT = 'taki:lia:screen';

/** Les ecrans qui ont publie, du plus ancien au plus recent, avec leur dernier etat. */
const published: { from: object; state: LiaScreenState }[] = [];

/**
 * Publie l'etat d'UN ecran. Plusieurs moteurs peuvent vivre ensemble (un ecran
 * et une sous-vue, une modale) : le dernier qui publie un etat en devient le
 * proprietaire, et seul le proprietaire efface en se demontant.
 */
export function publishLiaScreen(state: LiaScreenState | null, from?: object): void {
  if (typeof window === 'undefined') return;
  const index = from === undefined ? -1 : published.findIndex((entry) => entry.from === from);
  if (index >= 0) published.splice(index, 1);
  if (state !== null && from !== undefined) published.push({ from, state });
  // En se retirant, un ecran rend la parole a celui qu'il recouvrait (une modale qui se ferme).
  const current = state ?? published.at(-1)?.state ?? null;
  if (state === null && from !== undefined && index < 0) return;
  window.dispatchEvent(new CustomEvent<LiaScreenState | null>(LIA_SCREEN_EVENT, { detail: current }));
}

/** L'etat publie, borne : quelques libelles courts, jamais une liste entiere. */
export function liaScreenState(input: {
  appId?: string | null; doorId?: string | null; doorLabel?: string | null; view?: string | null; search?: string | null;
  filters?: readonly { id: string; label: string }[]; activeFilterIds?: readonly string[]; archives?: boolean; recordId?: string | null;
  recordTitle?: string | null;
  family?: string | null;
}): LiaScreenState | null {
  if (!input.doorId || !input.doorLabel) return null;
  const labels = (input.activeFilterIds ?? []).map((id) => input.filters?.find((filter) => filter.id === id)?.label)
    .filter((label): label is string => typeof label === 'string' && label.length > 0).slice(0, 8).map((label) => label.slice(0, 60));
  const search = input.search?.trim().slice(0, 100);
  return {
    ...(input.appId ? { appId: input.appId } : {}),
    doorId: input.doorId,
    doorLabel: input.doorLabel,
    ...(input.view ? { view: input.view } : {}),
    ...(search ? { search } : {}),
    ...(labels.length ? { filters: labels } : {}),
    ...(input.archives ? { archives: true } : {}),
    ...(input.recordId ? { recordId: input.recordId } : {}),
    ...(input.recordId && input.recordTitle?.trim() ? { recordTitle: input.recordTitle.trim().slice(0, 120) } : {}),
    ...(input.family ? { family: input.family } : {}),
  };
}
